export const getDialectOfLanguage = (languageCode: string | undefined) => {
  if (languageCode === undefined) {
    return '';
  }
  const dialect = new Intl.DisplayNames('en', {
    type: 'language',
    languageDisplay: 'dialect',
  });
  let result: string | undefined;
  try {
    result = dialect.of(languageCode);
    if (!result) {
      return '';
    }
  } catch (error) {
    if (error instanceof RangeError) {
      return '';
    }
    throw error;
  }

  // Return as it is is already in "Language (Region)" format such as "Spanish (United States)"
  if (/\([^)]+\)/.test(result)) {
    return result;
  }

  const parts = result.split(' ');

  // Format "British English" -> "English (United Kingdom)"
  // Format "American English" -> "English (United States)"
  if (parts.length >= 2) {
    const [dialect, ...languageParts] = parts;
    let regionName = dialect;
    if (dialect === 'British') {
      regionName = 'United Kingdom';
    } else if (dialect === 'American') {
      regionName = 'United States';
    }
    return `${languageParts.join(' ')} (${regionName})`;
  }

  return result;
};

export const getLanguageName = (languageCode: string) => {
  const languageNames = new Intl.DisplayNames(['en'], {
    type: 'language',
    languageDisplay: 'standard',
  });
  let result: string | undefined;
  try {
    result = languageNames.of(languageCode);
  } catch (error) {
    if (error instanceof RangeError) {
      return '';
    }
    throw error;
  }
  return result || '';
};

const ACCENT_TO_REGION: Record<string, string> = {
  ssbe: 'GB',
  british: 'GB',
  american: 'US',
  australian: 'AU',
  canadian: 'CA',
  irish: 'IE',
};

const regionToFlag = (region: string) =>
  region
    .toUpperCase()
    .split('')
    .map(letter => String.fromCodePoint(parseInt(letter, 36) - 10 + 0x1f1e6))
    .join('');

const GLOBAL_FLAG = '🌐';

export const getAccentLabel = (languageCode: string | undefined): string => {
  if (!languageCode) {
    return '';
  }

  const dialect = new Intl.DisplayNames('en', {
    type: 'language',
    languageDisplay: 'dialect',
  });

  let result: string | undefined;
  try {
    result = dialect.of(languageCode);
    if (!result) {
      return '';
    }
  } catch (error) {
    if (error instanceof RangeError) {
      return '';
    }
    throw error;
  }

  // "Spanish (United States)" -> extract region from parentheses
  const parenMatch = result.match(/\(([^)]+)\)/);
  if (parenMatch) {
    return parenMatch[1];
  }

  const parts = result.split(' ');
  if (parts.length >= 2) {
    const [dialect, ...languageParts] = parts;
    const language = languageParts.join(' ');

    // "American English" -> "American", "British English" -> "British"
    if (language === 'English') {
      return dialect;
    }

    // "Castilian Spanish" -> "Spanish"
    if (dialect === 'Castilian') {
      return language;
    }

    // "Brazilian Portuguese" -> "Brazilian Portuguese"
    // "Canadian French" -> "Canadian French"
    return result;
  }

  return result;
};

export const getFlagOfLanguage = (languageCode?: string) => {
  if (!languageCode) {
    return GLOBAL_FLAG;
  }

  // Handle BCP 47 codes like "en-GB"
  const parts = languageCode.split('-');
  if (parts[1]?.length === 2) {
    return regionToFlag(parts[1]);
  }

  // Handle free-form accent strings like "English, SSBE"
  const lower = languageCode.toLowerCase();
  for (const [keyword, region] of Object.entries(ACCENT_TO_REGION)) {
    if (lower.includes(keyword)) {
      return regionToFlag(region);
    }
  }

  return GLOBAL_FLAG;
};
